import { Context } from 'vuex-smart-module'
import VueRouter, { Route, NavigationGuardNext } from 'vue-router'
import { InitPluginsOptions } from '@/typings/common'
import { authStoreModule } from '@/views/Auth/store'
import { AUTH_ROUTES } from '@/views/Auth/utils'
import { ADMIN_LK } from '@/helpers/enums/menu-folders'

type AuthContext = Context<typeof authStoreModule>

const createRouterGuardProfile = (router: VueRouter, storeModule: AuthContext) => {
  // eslint-disable-next-line @typescript-eslint/ban-types
  let unwatch: Function

  const routerGuardProfile = async (to: Route, from: Route, next: NavigationGuardNext) => {
    unwatch?.()
    const match = to.matched.find((record) => 'authorized' in record.meta && !!record.meta.authorized)

    const authorized = match?.meta?.authorized
    const getProfile = storeModule.actions.getProfile()

    try {
      await getProfile
      next()
    } catch {
      if (!authorized) {
        next()
      } else {
        next({
          name: AUTH_ROUTES.LOGOUT,
          params: match?.parent?.name === ADMIN_LK.name ? { toAuth: authorized } : { toHome: authorized },
        })
      }
    }
  }

  const routerGuardAuthorized = (to: Route, from: Route, next: NavigationGuardNext) => {
    const match = to.matched.find((record) => 'authorized' in record.meta && !!record.meta.authorized)
    const { authorized } = storeModule.getters

    if (!match) return next()

    switch (match.meta.authorized) {
      case true:
        if (authorized || to.name === AUTH_ROUTES.LOGIN) {
          return next()
        }
        return next()

      case false:
        return authorized ? next('/') : next()

      default:
        return next()
    }
  }

  unwatch = router.beforeEach(routerGuardProfile)
  router.beforeEach(routerGuardAuthorized)
}

const createRouterGuardAuthorized = (router: VueRouter, storeModule: AuthContext) => {
  const routerGuardAuthorized = (to: Route, from: Route, next: NavigationGuardNext) => {
    const match = to.matched.find((record) => 'authorized' in record.meta && !!record.meta.authorized)
    const { authorized } = storeModule.getters

    if (!match) return next()

    switch (match.meta.authorized) {
      case true:
        if (authorized || to.name === AUTH_ROUTES.LOGIN) {
          return next()
        }
        return next()

      case false:
        return authorized ? next('/') : next()

      default:
        return next()
    }
  }

  router.beforeEach(routerGuardAuthorized)
}

export const initPlugin = (options: InitPluginsOptions) => {
  const { store, router } = options
  const auth = authStoreModule.context(store)
  // must bee first
  createRouterGuardProfile(router, auth)
  createRouterGuardAuthorized(router, auth)
}
